import { Card, EmptyState, PageHeader, PeriodSwitcher, QueryState } from "@nymbl/nos2-rules";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Trophy } from "lucide-react";
import { AwardSummary } from "@/components/incentives/AwardSummary";
import { GoalsTable } from "@/components/incentives/GoalsTable";
import { useIncentives } from "@/hooks/use-incentives";
import {
  defaultIncentiveYear,
  incentiveYearOptions,
  incentiveYearSteps,
} from "@/lib/incentives/incentives";
import type { IncentivesView } from "@/schemas/incentives";

/** My Incentives: the signed-in person's own scorecard, one year at a time (self only). */
export function IncentivesScreen() {
  const search = useSearch({ from: "/dashboard/incentives" });
  const navigate = useNavigate({ from: "/dashboard/incentives" });
  const incentives = useIncentives();
  const currentYear = new Date().getFullYear();
  const dataYears = incentives.data?.years.map((y) => y.year) ?? [];
  const fallback = incentives.data ? defaultIncentiveYear(dataYears, currentYear) : currentYear;
  const year = search.year ?? fallback;
  const options = incentiveYearOptions(dataYears, currentYear);
  const steps = incentiveYearSteps(year, options);

  function setYear(next: number) {
    void navigate({ search: { year: next === fallback ? undefined : next } });
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Incentives"
        period={
          <PeriodSwitcher
            unit="year"
            label={String(year)}
            isCurrent={year === currentYear}
            canPrev={incentives.isSuccess && steps.prev !== null}
            canNext={incentives.isSuccess && steps.next !== null}
            onPrev={() => steps.prev !== null && setYear(steps.prev)}
            onNext={() => steps.next !== null && setYear(steps.next)}
            onCurrent={() => setYear(currentYear)}
            labelClassName="w-14"
          />
        }
      />
      <QueryState query={incentives} skeletonClassName="h-48">
        {(view) => <IncentiveYearView view={view} year={year} />}
      </QueryState>
    </div>
  );
}

function IncentiveYearView({ view, year }: { view: IncentivesView; year: number }) {
  const data = view.years.find((y) => y.year === year);
  if (!data || data.companyGoals.length + data.personalGoals.length === 0) {
    return (
      <Card>
        <EmptyState icon={Trophy} title={`No scorecard for ${year}`} />
      </Card>
    );
  }
  return (
    <>
      <AwardSummary year={data} />
      {data.companyGoals.length > 0 && (
        <GoalsTable key={`c-${year}`} title="Company goals" goals={data.companyGoals} year={year} />
      )}
      {data.personalGoals.length > 0 && (
        <GoalsTable
          key={`p-${year}`}
          title="Personal goals"
          goals={data.personalGoals}
          year={year}
        />
      )}
    </>
  );
}
